import { useApolloClient } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '@paintscout/ui'
import { contentHash } from '@paintscout/util/builder'
import { useQuote } from '../../../context'
import { getCachedTranslation, setCachedTranslation } from './useTranslatedNoteContent.cache'

// Augment here to guarantee visibility in all webpack/fork-ts-checker
// compilation contexts (symlinked @types packages can be missed by fork-ts-checker v6).
declare module 'paintscout' {
  export interface QuoteDocument {
    translations?: Record<string, Record<string, string>>
  }
}

const TRANSLATE_NOTE_CONTENT = gql`
  query translateNoteContent($notes: [String!]!, $lang: String!) {
    translateNoteContent(notes: $notes, lang: $lang)
  }
`

const EMPTY_TRANSLATIONS: Record<string, string> = {}
const HAS_ALPHA_CHARACTER = /[a-z]/i

// Tracks how many useTranslatedNoteContent instances are actively fetching translations.
// Written to document.body so the PDF pdfReady poll can gate on it.
let translationLoadingCount = 0

type PendingTranslation = {
  content: string
  lang: string
  apolloClient: ReturnType<typeof useApolloClient>
  resolve: (value: string) => void
  reject: (error: unknown) => void
}

// All work-order note hooks mount in the same React commit. Queue their misses for one microtask
// and batch them into the server's 50-note/20k-character envelope. The key also acts as a
// single-flight registry, so duplicate strings mounted in separate cards share one request.
const pendingTranslations = new Map<string, PendingTranslation>()
let translationFlushScheduled = false

function requestBatchedTranslations(
  apolloClient: ReturnType<typeof useApolloClient>,
  lang: string,
  contents: string[]
): Promise<string[]> {
  const requests = contents.map((content) => {
    const key = getCacheKey(lang, content)
    const cached = getCachedTranslation(key)
    if (cached != null) return Promise.resolve(cached)

    const existing = pendingTranslations.get(key)
    if (existing) {
      return new Promise<string>((resolve, reject) => {
        const priorResolve = existing.resolve
        const priorReject = existing.reject
        existing.resolve = (value) => {
          priorResolve(value)
          resolve(value)
        }
        existing.reject = (error) => {
          priorReject(error)
          reject(error)
        }
      })
    }

    return new Promise<string>((resolve, reject) => {
      pendingTranslations.set(key, { content, lang, apolloClient, resolve, reject })
      scheduleTranslationFlush()
    })
  })
  return Promise.all(requests)
}

function scheduleTranslationFlush() {
  if (translationFlushScheduled) return
  translationFlushScheduled = true
  queueMicrotask(() => void flushPendingTranslations())
}

async function flushPendingTranslations() {
  translationFlushScheduled = false
  const pending = Array.from(pendingTranslations.entries())
  pending.forEach(([key]) => pendingTranslations.delete(key))

  const groups = new Map<string, Array<[string, PendingTranslation]>>()
  for (const entry of pending) {
    const group = groups.get(entry[1].lang) ?? []
    group.push(entry)
    groups.set(entry[1].lang, group)
  }

  await Promise.all(
    Array.from(groups.entries()).map(async ([lang, entries]) => {
      let batch: Array<[string, PendingTranslation]> = []
      let chars = 0
      const flushBatch = async () => {
        if (!batch.length) return
        const current = batch
        batch = []
        chars = 0
        try {
          const { data } = await current[0][1].apolloClient.query<{ translateNoteContent: string[] }>({
            query: TRANSLATE_NOTE_CONTENT,
            variables: { notes: current.map(([, item]) => item.content), lang },
            fetchPolicy: 'no-cache'
          })
          current.forEach(([key, item], index) => {
            const value = data?.translateNoteContent?.[index] ?? item.content
            setCachedTranslation(key, value)
            item.resolve(value)
          })
        } catch (error) {
          current.forEach(([, item]) => item.reject(error))
        }
      }

      for (const entry of entries) {
        const length = entry[1].content.length
        if (batch.length >= 50 || (batch.length && chars + length > 20_000)) await flushBatch()
        batch.push(entry)
        chars += length
      }
      await flushBatch()
    })
  )
}

function incrementTranslationLoading() {
  translationLoadingCount++
  if (typeof document !== 'undefined') {
    document.body.dataset.translationsLoading = 'true'
  }
}

function decrementTranslationLoading() {
  translationLoadingCount = Math.max(0, translationLoadingCount - 1)
  if (translationLoadingCount === 0 && typeof document !== 'undefined') {
    document.body.dataset.translationsLoading = 'false'
  }
}

export function useTranslatedNoteContent(contents: Array<string | null | undefined>) {
  const apolloClient = useApolloClient()
  const { lang } = useTranslation()
  const { quote, isCustomerView } = useQuote()
  // The pdf flag comes from the request URL and never changes during a render session,
  // so parse it once instead of on every hook call (a work order mounts this hook for
  // every note field).
  const isPdfRender = useMemo(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('pdf') === '1',
    []
  )

  const normalizedContents = contents.map((content) => content ?? '')
  const normalizedContentsKey = normalizedContents.join('\u0001')
  const preBakedTranslations = useMemo(
    () => quote?.translations?.[lang] ?? EMPTY_TRANSLATIONS,
    [lang, quote?.translations]
  )
  const preBakedTranslationsKey = normalizedContents
    .map((content) => preBakedTranslations[contentHash(content)] ?? '')
    .join('\u0001')
  const initialTranslatedContents = useMemo(() => {
    seedTranslationCache({ lang, normalizedContents, preBaked: preBakedTranslations })
    return getVisibleContents({
      lang,
      normalizedContents,
      preBaked: preBakedTranslations,
      hideUncached: !isPdfRender
    })
  }, [isPdfRender, lang, normalizedContentsKey, preBakedTranslationsKey])
  const [translatedContents, setTranslatedContents] = useState<string[]>(initialTranslatedContents)
  // Set to true in the async translate() finally block, cleared in the post-render
  // useEffect below. This ensures decrementTranslationLoading() is called only after
  // React has committed translated content to the DOM (Skeletons gone), so pdfReady
  // does not fire while loading bars are still visible.
  const pendingDecrementRef = useRef(false)

  const loading = useMemo(() => {
    if (!lang || lang === 'en') return false
    if (isPdfRender) return false
    return normalizedContents.some((content, i) => {
      if (!content.trim() || !HAS_ALPHA_CHARACTER.test(content)) return false
      return translatedContents[i] === ''
    })
  }, [isPdfRender, lang, normalizedContentsKey, translatedContents])

  useEffect(() => {
    setTranslatedContents((current) =>
      areContentsEqual(current, initialTranslatedContents) ? current : initialTranslatedContents
    )

    if (!lang || lang === 'en' || isPdfRender) {
      return
    }

    // Live note translation (translateNoteContent) is guarded to customer-view only, so it
    // can never run in the in-app work-order dialog. Outside customer-view we skip the query
    // entirely and let uncached content stay blank (Skeletons) until the pre-baked
    // quote.translations[lang] arrives — e.g. while the deal dialog generates translations.
    // Firing the doomed query here would also poison the in-memory cache with the English
    // fallback and prevent the freshly pre-baked text from ever rendering.
    if (!isCustomerView) {
      return
    }

    const uniqueContents = Array.from(
      new Set(normalizedContents.filter((content) => content.trim() && HAS_ALPHA_CHARACTER.test(content)))
    )

    const uncached = uniqueContents.filter((content) => !getCachedTranslation(getCacheKey(lang, content)))
    let cancelled = false
    // Track whether we incremented so the cleanup and the finally can both
    // guard against double-decrement — whichever fires first wins.
    let translationPending = uncached.length > 0

    if (translationPending) incrementTranslationLoading()

    async function translate() {
      try {
        if (uncached.length) {
          const translated = await requestBatchedTranslations(apolloClient, lang, uncached)
          uncached.forEach((content, index) => {
            setCachedTranslation(getCacheKey(lang, content), translated[index] ?? content)
          })
        }
      } catch {
        uncached.forEach((content) => {
          setCachedTranslation(getCacheKey(lang, content), content)
        })
      } finally {
        // Mark for a post-render decrement rather than decrementing immediately.
        // Decrementing here (before setTranslatedContents triggers a re-render)
        // would clear data-translations-loading while Skeleton loaders are still
        // in the DOM, letting pdfReady fire too early.
        if (translationPending) {
          translationPending = false
          pendingDecrementRef.current = true
        }
      }

      if (cancelled) {
        return
      }

      setTranslatedContents(
        normalizedContents.map((content) => getCachedTranslation(getCacheKey(lang, content)) ?? content)
      )
    }

    void translate()

    return () => {
      cancelled = true
      // If the async query is still in-flight when this effect is torn down,
      // decrement immediately so pdfReady isn't blocked after unmount.
      if (translationPending) {
        translationPending = false
        decrementTranslationLoading()
      }
      // If translate() already finished but the post-render effect hasn't fired
      // yet (e.g. component unmounted between finally and the next paint), clear
      // the pending decrement here to avoid a double-decrement in the effect.
      if (pendingDecrementRef.current) {
        pendingDecrementRef.current = false
        decrementTranslationLoading()
      }
    }
  }, [apolloClient, isCustomerView, isPdfRender, lang, normalizedContentsKey, preBakedTranslationsKey])

  // Fires after every commit. When translate() has finished and React has painted
  // the translated content (Skeletons gone), flush the pending decrement so
  // data-translations-loading clears only once the DOM reflects translated text.
  useEffect(() => {
    if (pendingDecrementRef.current) {
      pendingDecrementRef.current = false
      decrementTranslationLoading()
    }
  })

  return [translatedContents, loading] as [string[], boolean]
}

function getCacheKey(lang: string, content: string) {
  return `${lang}::${content}`
}

function seedTranslationCache({
  lang,
  normalizedContents,
  preBaked
}: {
  lang?: string
  normalizedContents: string[]
  preBaked: Record<string, string>
}) {
  if (!lang || lang === 'en') {
    return
  }

  normalizedContents
    .filter((content) => content.trim() && HAS_ALPHA_CHARACTER.test(content))
    .forEach((content) => {
      const key = getCacheKey(lang, content)
      if (!getCachedTranslation(key)) {
        const preBakedValue = preBaked[contentHash(content)]
        if (preBakedValue) {
          setCachedTranslation(key, preBakedValue)
        }
      }
    })
}

function getVisibleContents({
  lang,
  normalizedContents,
  preBaked,
  hideUncached
}: {
  lang?: string
  normalizedContents: string[]
  preBaked: Record<string, string>
  hideUncached: boolean
}) {
  if (!lang || lang === 'en') {
    return normalizedContents
  }

  return normalizedContents.map((content) => {
    if (!content.trim() || !HAS_ALPHA_CHARACTER.test(content)) {
      return content
    }

    const cached = getCachedTranslation(getCacheKey(lang, content))
    if (cached != null) {
      return cached
    }

    const preBakedValue = preBaked[contentHash(content)]
    if (preBakedValue != null) {
      return preBakedValue
    }

    return hideUncached ? '' : content
  })
}

function areContentsEqual(left: string[], right: string[]) {
  return left.length === right.length && left.every((value, index) => value === right[index])
}
